/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { BaseSyntheticEvent, useEffect, useMemo, useRef } from "react";
import { styled, main } from "@iventis/styles";
import * as d3 from "d3";
import { Group } from "@iventis/types";

import { TimeAxis } from "./time-axis";
import { ContextData, InteractionPhase, CalendarContext, CalendarCursor, CalendarTheme } from "./calendar-context";
import { GroupAxis } from "./group-axis";
import { Session, SessionComponent } from "./types";
import { CalendarPlot } from "./calendar-plot";
import { DropShadowFilter } from "./filters";
import { ZoomContainer } from "./zoom-container";
import { useThrottledResizeObserver } from "./hooks";
import { MAX_ZOOM_IN, MAX_ZOOM_OUT } from "../utilities/constants";

export interface CalendarProps {
    minCategoryWidth: number;
    data: Session[];
    groups: Group[];
    start: Date;
    end: Date;
    className?: string;
    showSubEvents?: boolean;
    sessionComponent: SessionComponent;
    eventUnitComponent?: SessionComponent;
    sessionTabFormatter?: (session: Session) => string;
    onSessionClick?: (session: Session, event: PointerEvent) => void;
    onCalendarClick?: (group: Group, time: Date) => void;
    onSessionContextClick?: (session: Session, event: PointerEvent) => void;
    onCalendarContextClick?: (group: Group, time: Date, event: PointerEvent) => void;
    onKeyButtonClicked?: (event: BaseSyntheticEvent) => void;
    keyOpen?: boolean;
    calendarTheme: CalendarTheme;
    onTimeChange?: (update: { start: Date; end: Date }) => void;
    translations?: Partial<{ resetZoom: string }>;
    initialStart?: Date;
    initialEnd?: Date;
}

export const Calendar: React.FC<CalendarProps> = ({
    groups,
    start,
    end,
    minCategoryWidth,
    data,
    onSessionClick,
    onCalendarClick,
    sessionTabFormatter,
    sessionComponent,
    eventUnitComponent,
    showSubEvents,
    onCalendarContextClick,
    onSessionContextClick,
    onKeyButtonClicked,
    keyOpen,
    onTimeChange,
    initialStart,
    initialEnd,
    calendarTheme: theme = { iconButtonColours: { idle: main.primaryColors.subdued70, selected: main.primaryColors.focus } },
    translations,
}) => {
    const svgRef = useRef();
    const contextData = useMemo(() => new ContextData(), []);

    const { ref, width = 0, height = 0 } = useThrottledResizeObserver<HTMLDivElement>(20);

    const zoomBehaviour = useMemo(() => d3.zoom<SVGGraphicsElement, unknown>().scaleExtent([MAX_ZOOM_OUT, MAX_ZOOM_IN]), []);

    useEffect(() => contextData.updateTheme(theme), [theme]);

    useEffect(() => {
        contextData.updateCalendarProps(data, groups, start, end, minCategoryWidth, sessionComponent, eventUnitComponent, sessionTabFormatter, showSubEvents);
    }, [groups, start, end, minCategoryWidth, data, contextData, showSubEvents, sessionComponent, eventUnitComponent, sessionTabFormatter]);

    let zoomEventRequested = false;

    // Zoom Behaviour
    useEffect(() => {
        contextData.updateViewportSize(height, width);
        zoomBehaviour
            .clickDistance(5)
            .translateExtent([
                [0, 0],
                [width, height],
            ])
            .on("zoom", (event) => {
                // Throttle the number of zoom requests using d3 timeout
                // This improves performance on larger schedules
                if (!zoomEventRequested) {
                    zoomEventRequested = true;
                    d3.timeout(() => {
                        zoomEventRequested = false;
                        contextData.updateZoom(event);
                    }, 16.7); // 16.7 second updates == 60 updates per second
                }
            })
            .on("zoom.dblclick", null)
            .on("start", (event) => {
                if (event.sourceEvent?.type === "mousedown") {
                    contextData.cursor$.next(CalendarCursor.GRABBING);
                }
                contextData.updateInteractionPhase(InteractionPhase.START);
                contextData.updateZoom(event);
            })
            .on("end", (event) => {
                if (event.sourceEvent?.type === "mouseup") {
                    contextData.cursor$.next(CalendarCursor.GRAB);
                }
                contextData.updateInteractionPhase(InteractionPhase.END);
            });
    }, [height, width, zoomBehaviour]);

    // No users screen will be width 0 and heeight 0, so we use these as initial values to know that we're still formatting the calendar. Set state to idle once width and height have been set
    useEffect(() => contextData.updateState(height !== 0 && width !== 0 ? "idle" : "formatting-calendar"), [width, height]);

    return (
        <CalendarContext.Provider value={contextData}>
            <CalendarContainer data-cy="calendar" ref={ref}>
                <SvgContainer data-cy="calendar-svg" ref={svgRef} height={height} width={width} viewBox={`0 0 ${width} ${height}`}>
                    <defs>
                        <DropShadowFilter />
                    </defs>
                    <TimeAxis onTimeChange={onTimeChange} />
                    <GroupAxis />
                    <ZoomContainer
                        onClick={onCalendarClick}
                        zoomBehavior={zoomBehaviour}
                        onContextClick={onCalendarContextClick}
                        onKeyButtonClicked={onKeyButtonClicked}
                        keyOpen={keyOpen}
                        resetZoomText={translations?.resetZoom ?? "Reset zoom"}
                        initialStart={initialStart}
                        initialEnd={initialEnd}
                    >
                        <CalendarPlot data={data} onSessionClick={onSessionClick} onSessionContextClick={onSessionContextClick} />
                    </ZoomContainer>
                </SvgContainer>
            </CalendarContainer>
        </CalendarContext.Provider>
    );
};

const CalendarContainer = styled.div`
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 100%;
    flex: 1;
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    border-width: 0;
`;

const SvgContainer = styled.svg`
    margin: 0;
    display: block;
    padding: 0;
    border-width: 0;
`;
